import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Alert, notification, Spin } from 'antd';
import documentSettingsService from '../../../services/documentSettingsService';

const { Title, Text } = Typography;

// Los mismos que exige el backend para emitir una factura.
const ISSUER_REQUIRED = ['issuer_name', 'issuer_tax_id', 'tax_regime', 'phone', 'address', 'municipality', 'department'];
const ISSUER_FIELDS = ['trade_name', ...ISSUER_REQUIRED];

const required = { required: true, whitespace: true, message: 'Campo requerido' };

const isIssuerComplete = (settings) => ISSUER_REQUIRED.every((field) => settings?.[field]?.trim());

const DocumentsSection = () => {
  const [form] = Form.useForm();
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingBlock, setSavingBlock] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await documentSettingsService.getDocumentSettings();
        setSaved(response.data);
        form.setFieldsValue(response.data);
      } catch {
        notification.error({ title: 'Error', description: 'No se pudo cargar la configuración de documentos.' });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [form]);

  const issuerComplete = isIssuerComplete(saved);

  // El PUT exige siempre los datos del emisor: los bloques ICA y asistente
  // envían los ya guardados, no lo que esté a medio editar en el emisor.
  // Los opcionales de otros bloques no se envían y el backend los conserva.
  const saveBlock = async (block, fields) => {
    // Si un campo no es válido, el formulario ya muestra el error.
    const values = await form.validateFields(fields).catch(() => null);
    if (!values) return;
    const body = block === 'issuer'
      ? values
      : { ...Object.fromEntries(ISSUER_REQUIRED.map((field) => [field, saved[field]])), ...values };

    setSavingBlock(block);
    try {
      const response = await documentSettingsService.updateDocumentSettings(body);
      setSaved(response.data);
      form.setFieldsValue(Object.fromEntries(fields.map((field) => [field, response.data[field]])));
      notification.success({ title: 'Configuración de documentos actualizada' });
    } catch (error) {
      notification.error({
        title: 'Error',
        description: error.response?.data?.mensaje || 'No se pudo guardar la configuración de documentos.',
      });
    } finally {
      setSavingBlock(null);
    }
  };

  const saveButton = (block, fields, disabled = false) => (
    <>
      <Button
        type="primary"
        onClick={() => saveBlock(block, fields)}
        loading={savingBlock === block}
        disabled={disabled}
        style={disabled ? undefined : { backgroundColor: '#1a3c2e' }}
      >
        Guardar
      </Button>
      {disabled && !loading && (
        <Text type="secondary" style={{ marginLeft: 12 }}>Guarde primero los datos del emisor</Text>
      )}
    </>
  );

  const cardStyle = { maxWidth: 520, marginBottom: 16 };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={4} style={{ color: '#595959', fontWeight: 600 }}>
        Documentos
      </Title>

      <Spin spinning={loading}>
        {!loading && saved && !issuerComplete && (
          <Alert
            type="warning"
            showIcon
            title="Complete los datos del emisor para poder facturar"
            style={{ maxWidth: 520, marginBottom: 16 }}
          />
        )}

        <Form form={form} layout="vertical">
          <Card title="Datos del emisor" style={cardStyle}>
            <Form.Item
              name="trade_name"
              label="Nombre comercial"
              extra="Opcional. Si se deja vacío, los documentos destacan el nombre del representante."
            >
              <Input maxLength={150} />
            </Form.Item>
            <Form.Item name="issuer_name" label="Nombre del representante" rules={[required]}>
              <Input maxLength={150} />
            </Form.Item>
            <Form.Item name="issuer_tax_id" label="NIT" rules={[required]}>
              <Input maxLength={30} />
            </Form.Item>
            <Form.Item name="tax_regime" label="Régimen tributario" rules={[required]}>
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="phone" label="Teléfono" rules={[required]}>
              <Input maxLength={30} />
            </Form.Item>
            <Form.Item name="address" label="Dirección" rules={[required]}>
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="municipality" label="Municipio" rules={[required]}>
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="department" label="Departamento" rules={[required]}>
              <Input maxLength={100} />
            </Form.Item>
            {saveButton('issuer', ISSUER_FIELDS)}
          </Card>

          <Card title="Registro ICA" style={cardStyle}>
            <Form.Item name="ica_property_number" label="Número de predio" extra="Opcional si no tiene finca.">
              <Input maxLength={30} />
            </Form.Item>
            {saveButton('ica', ['ica_property_number'], !issuerComplete)}
          </Card>

          <Card title="Asistente técnico" style={cardStyle}>
            <Form.Item name="technical_assistant_name" label="Nombre" extra="Opcional.">
              <Input maxLength={150} />
            </Form.Item>
            {saveButton('assistant', ['technical_assistant_name'], !issuerComplete)}
          </Card>
        </Form>
      </Spin>
    </div>
  );
};

export default DocumentsSection;
